/**
 * Public viewer for published canvases: a read-only, infinitely zoomable
 * web page (Endless Paper's "publish your canvas as an interactive
 * website"). Loads the document via the public /published/{token} endpoint —
 * no account required.
 */

import { FrondEngine } from "@frond/engine";
import { loadConfig } from "@frond/config";
import { FrondClient } from "@frond/client";
import { useEffect, useRef, useState } from "react";

interface Props {
  token: string;
}

export function Viewer({ token }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [engine, setEngine] = useState<FrondEngine | null>(null);
  const [status, setStatus] = useState<"loading" | "error" | "ready">("loading");
  const [name, setName] = useState("");

  useEffect(() => {
    let disposed = false;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const engine = new FrondEngine({ canvas, background: "#ffffff" });
    setEngine(engine);

    void (async () => {
      try {
        const config = await loadConfig();
        const client = new FrondClient(config, async () => null); // public: no auth
        const ref = await client.getPublished(token);
        const res = await fetch(ref.downloadUrl);
        if (!res.ok) throw new Error("document unavailable");
        const json = await res.text();
        if (disposed) return;
        engine.load(json);
        engine.zoomToContent();
        setName(ref.meta.name);
        setStatus("ready");
      } catch {
        if (!disposed) setStatus("error");
      }
    })();

    return () => {
      disposed = true;
      engine.dispose();
    };
  }, [token]);

  return (
    <div className="viewer">
      <div className="viewer-canvas">
        <canvas ref={canvasRef} />
      </div>
      <header className="viewer-bar">
        <span className="logo">🌿</span>
        <span className="viewer-name">{name || "Published canvas"}</span>
        <span className="viewer-hint">Pinch / scroll to zoom — zoom forever</span>
        <a className="btn subtle" href={appHome()} onClick={backToApp}>
          Open in the canvas
        </a>
      </header>
      {engine && (
        <div className="viewer-zoom">
          <button onClick={() => zoom(engine, 1 / 1.5)}>−</button>
          <button onClick={() => zoom(engine, 1.5)}>+</button>
          <button onClick={() => engine.zoomToContent()}>⛶</button>
        </div>
      )}
      {status === "loading" && <div className="viewer-status">Loading canvas…</div>}
      {status === "error" && (
        <div className="viewer-status">
          This canvas is not available (unpublished or removed).
        </div>
      )}
    </div>
  );
}

function zoom(engine: FrondEngine, f: number): void {
  const { w, h } = engine.renderer.size;
  engine.camera.zoomAt(w / 2, h / 2, { x: w / 2, y: h / 2 }, f);
  engine.requestRender();
}

/** "Open in the canvas" resolves via the same origin's config. */
function appHome(): string {
  try {
    const cfg = (window as unknown as { __FROND_CONFIG__?: { links?: { app?: string } } })
      .__FROND_CONFIG__;
    if (cfg?.links?.app) return cfg.links.app;
  } catch {
    /* fall through */
  }
  return "/";
}

function backToApp(): void {
  // The viewer is served from the app origin; "/" is the canvas itself.
  window.location.href = "/";
}
